import { fmtDate, type UnitStatusView } from './derive';
import type { ResolvedUnitStatus } from './resolve';

/**
 * The human wording for each part of a unit's status.
 *
 * Shared so the badge chips and the spreadsheet export say exactly the same
 * thing — a report that reads "Notice given" while the UI says "Move out 9/1"
 * is the kind of drift that makes people distrust both.
 */

type AnyStatus = UnitStatusView | ResolvedUnitStatus;

/**
 * How the current tenant's renewal reads.
 *
 * A renewal is executed well before its term opens, so "Renewed" is wrong until
 * the new lease actually starts — until then it's signed and pending.
 */
export function renewalLabel(status: AnyStatus, now: Date = new Date()): string | null {
  const current = status.current;
  if (!current) return null;

  if (current.renewed) {
    const effective = (status as ResolvedUnitStatus).renewalEffectiveDate;
    if (effective && new Date(effective).getTime() > now.getTime()) {
      return `Renewal signed · from ${fmtDate(effective)}`;
    }
    return 'Renewed';
  }
  if (current.renewal === 'negotiating') return 'Renewal negotiating';
  if (current.renewal === 'terms_agreed') return 'Terms agreed';
  return null;
}

/** How a scheduled move-out reads. */
export function moveOutLabel(status: AnyStatus): string | null {
  const moveOut = status.current?.moveOut;
  if (!moveOut) return null;
  return moveOut.date ? `Move out ${fmtDate(moveOut.date)}` : 'Move out scheduled';
}

/**
 * How the incoming tenant reads, if there is one.
 *
 * A scheduled move-in is the most concrete thing we can say, so it wins. Failing
 * that, report where the lease is in the pipeline — a drafted lease and an
 * executed one are very different commitments.
 */
export function incomingLabel(status: AnyStatus): string | null {
  const incoming = status.incoming;
  if (!incoming) return null;
  if (incoming.moveIn?.date) return `Move in ${fmtDate(incoming.moveIn.date)}`;
  switch (incoming.lease) {
    case 'created':
      return 'Lease drafted';
    case 'sent':
      return 'Lease sent';
    case 'executed':
      return 'Lease signed';
    default:
      return incoming.moveIn ? 'Move in scheduled' : null;
  }
}

/** How the listing state reads. */
export function listingLabel(status: AnyStatus): string | null {
  if (status.listing === 'listed') return 'Listed';
  if (status.listing === 'showing') return 'Showing';
  return null;
}

/**
 * Everything known about the *sitting* tenant, as one cell.
 *
 * A unit can be mid-renewal and have given notice at the same time, so these
 * join rather than pick a winner.
 */
export function currentTenantStatusLabel(status: AnyStatus, now?: Date): string {
  const parts = [renewalLabel(status, now), moveOutLabel(status)].filter(Boolean);
  return parts.length ? parts.join('; ') : '';
}

/** Everything known about the *next* tenant, as one cell. */
export function nextTenantStatusLabel(status: AnyStatus): string {
  const incoming = incomingLabel(status);
  if (!incoming) return '';
  const name = status.incoming?.tenantName;
  return name ? `${incoming} (${name})` : incoming;
}
